import { useHandleResponse } from '@/utils/useHandleResponse';
import { useMutation } from '@tanstack/react-query';
import { App, AutoComplete, Button, Col, Divider, Form, Input, Row, Select } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { FC, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateField, updateProps } from '../../../contextRedux/Provider/ProviderModel';
import { Information } from '../../../DTOS/CompanyInformation/Information';
import CompanyInformationService from '../../../services/companyInformation/CompanyInformationService';
import ProviderService from '../../../services/provider/ProviderService';
import { RootState } from '../../../store';
import './ProviderForm.css';

const ProviderForm: FC<any> = () => {
  const dispatch = useDispatch();
  const [form] = useForm();
  const { message } = App.useApp();
  const handleResponse = useHandleResponse();

  const { Option } = Select;

  const provider = useSelector((state: RootState) => state.models.provider);
  const providerGroups = useSelector((state: RootState) => state.store.providerGroup.data);

  const initialValues = {
    id: useSelector((state: RootState) => state?.models?.provider?.id),
    providerGroupId: useSelector((state: RootState) => state?.models?.provider?.providerGroupId),
    type: useSelector((state: RootState) => state?.models?.provider?.type),
    name: useSelector((state: RootState) => state?.models?.provider?.name),
    country: useSelector((state: RootState) => state?.models?.provider?.country),
    locality: useSelector((state: RootState) => state?.models?.provider?.locality),
    address: useSelector((state: RootState) => state?.models?.provider?.address),
    phone: useSelector((state: RootState) => state?.models?.provider?.phone),
    email: useSelector((state: RootState) => state?.models?.provider?.email),
    bankAccount: useSelector((state: RootState) => state?.models?.provider?.bankAccount),
    bank: useSelector((state: RootState) => state?.models?.provider?.bank),
    cnp: useSelector((state: RootState) => state?.models?.provider?.cnp),
    signature: useSelector((state: RootState) => state?.models?.provider?.signature),
    cui: useSelector((state: RootState) => state?.models?.provider?.cui),
    registrationNumber: useSelector((state: RootState) => state?.models?.provider?.registrationNumber),
    activityDescription: useSelector((state: RootState) => state?.models?.provider?.activityDescription),
    leadership: useSelector((state: RootState) => state?.models?.provider?.leadership),
    webAddress: useSelector((state: RootState) => state?.models?.provider?.webAddress),
  };

  useEffect(() => {
    const fieldsValue = {
      ...form.getFieldsValue(true),
      type: 'Persoana Juridica',
    };
    form.setFieldsValue(fieldsValue);
  }, []);

  const handleSelectGroupName = (value: string) => {
    const providerGroup: any = providerGroups?.find((group: any) => group.name === value)!;

    form.setFieldsValue({
      ...form.getFieldsValue(true),
      providerGroupId: providerGroup?.id,
      providerGroup: providerGroup,
    });

    dispatch(updateField({ field: 'providerGroupId', value: providerGroup?.id }));
    dispatch(updateField({ field: 'providerGroup', value: providerGroup }));
  };

  const handleCUIChange = (value: string) => {
    const specification: Information = {
      cui: value,
    };
    CompanyInformationService.informations(specification)
      .then((data: any) => {
        if (data.length === 1) {
          const fieldsValue = {
            ...form.getFieldsValue(true),
            name: data[0]?.name,
            address: data[0]?.fullAddress,
            registrationNumber: data[0]?.registrationCode,
          };

          form.setFieldsValue(fieldsValue);
          dispatch(updateField({ field: 'name', value: data[0]?.name }));
          dispatch(updateField({ field: 'address', value: data[0]?.fullAddress }));
          dispatch(
            updateField({
              field: 'registrationNumber',
              value: data[0]?.registrationCode,
            })
          );
        } else {
          const fieldsValue = {
            ...form.getFieldsValue(true),
            name: null,
            address: null,
            registrationNumber: null,
          };
          form.setFieldsValue(fieldsValue);
          dispatch(updateField({ field: 'name', value: null }));
          dispatch(updateField({ field: 'address', value: null }));
          dispatch(
            updateField({
              field: 'registrationNumber',
              value: null,
            })
          );
        }
      })
      .catch(() => {
        const fieldsValue = {
          ...form.getFieldsValue(true),
          name: null,
          address: null,
          registrationNumber: null,
        };
        form.setFieldsValue(fieldsValue);
        dispatch(updateField({ field: 'name', value: null }));
        dispatch(updateField({ field: 'address', value: null }));
        dispatch(
          updateField({
            field: 'registrationNumber',
            value: null,
          })
        );
      });
  };

  const handleBankAccountChange = (value: string) => {
    const specification: Information = {
      iban: value,
    };
    CompanyInformationService.bank(specification)
      .then((data) => {
        const fieldsValue = {
          ...form.getFieldsValue(true),
          bank: data?.bank_name,
        };
        form.setFieldsValue(fieldsValue);
      })
      .catch(() => {
        const fieldsValue = {
          ...form.getFieldsValue(true),
          bank: null,
        };
        form.setFieldsValue(fieldsValue);
      });
  };

  const handleFilter = (inputValue: string, option: any) => {
    return option?.value?.toLowerCase().indexOf(inputValue.toLowerCase()) !== -1;
  };

  const onSaveProviderType = async () => {
    saveProviderType(provider);
  };

  const { mutate: saveProviderType, isPending: _isSavingProvider } = useMutation({
    mutationFn: (formData: any) => ProviderService.upsert(provider.id, formData),
    onSuccess: (data: any) => {
      if (data) {
        handleResponse(data, data?.response?.name);
        dispatch(updateProps({ field: 'isModalVisible', value: false }));
        dispatch(updateProps({ field: 'refresh', value: !provider.props.refresh }));
      }
    },
    onError: (error: any) => {
      message.error(error.message);
    },
  });

  const handleFieldChange = (changedValues: any, _allValues: any) => {
    for (const key in changedValues) {
      dispatch(updateField({ field: key, value: changedValues[key] }));
    }
  };

  useEffect(() => {
    form.setFieldsValue(provider);
  }, [provider]);

  return (
    <div>
      <Form
        form={form}
        name='laborForm'
        initialValues={initialValues}
        onValuesChange={handleFieldChange}
        onFinish={onSaveProviderType}
        layout='vertical'
        className='providerForm'
      >
        <Divider />
        <Row gutter={16}>
          {/* Persoana Juridica */}
          <Col xs={24} sm={12} md={8}>
            <Form.Item label='Tip' name='type' rules={[{ required: true, message: 'Introdu tipul!' }]}>
              <AutoComplete filterOption={handleFilter}>
                {['Persoana Fizica', 'Persoana Juridica'].map((typeItem, index) => (
                  <Option key={index} value={typeItem}>
                    {typeItem}
                  </Option>
                ))}
              </AutoComplete>
            </Form.Item>
          </Col>
          {provider?.type === 'Persoana Juridica' && (
            <>
              <Col xs={24} sm={12} md={8}>
                <Form.Item label='Denumire' name='name' rules={[{ required: true, message: 'Introdu denumire!' }]}>
                  <Input />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={8}>
                <Form.Item label='CUI' name='cui' rules={[{ required: true, message: 'Introdu CUI!' }]}>
                  <Input onChange={(e) => handleCUIChange(e.target.value)} />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  label='Numar inregistrare'
                  name='registrationNumber'
                  rules={[{ required: true, message: 'Introdu numar inregistrare!' }]}
                >
                  <Input />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={8}>
                <Form.Item label='IBAN' name='bankAccount'>
                  <Input onChange={(e) => handleBankAccountChange(e.target.value)} />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={8}>
                <Form.Item label='Banca' name='bank'>
                  <Input />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={8}>
                <Form.Item label='Adresa' name='address' rules={[{ required: true, message: 'Introdu adresa!' }]}>
                  <Input />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={8}>
                <Form.Item label='Grup' name={['companyGroup', 'name']}>
                  <AutoComplete onSelect={handleSelectGroupName} filterOption={handleFilter}>
                    {providerGroups?.map((typeItem: { name: string }, index: any) => (
                      <Option key={index} value={typeItem.name}>
                        {typeItem.name}
                      </Option>
                    ))}
                  </AutoComplete>
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={8}>
                <Form.Item label='Telefon' name='phone' rules={[{ required: true, message: 'Introdu telefon!' }]}>
                  <Input />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={8}>
                <Form.Item label='Email' name='email' rules={[{ required: true, message: 'Introdu email!' }]}>
                  <Input />
                </Form.Item>
              </Col>
            </>
          )}

          {/* Persoana Fizica */}
          {provider?.type === 'Persoana Fizica' && (
            <>
              <Col xs={24} sm={12} md={8}>
                <Form.Item label='Denumire' name='name' rules={[{ required: true, message: 'Introdu denumire!' }]}>
                  <Input />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={8}>
                <Form.Item label='CNP' name='cnp' rules={[{ required: true, message: 'Introdu CNP!' }]}>
                  <Input />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={8}>
                <Form.Item label='Adresa' name='address' rules={[{ required: true, message: 'Introdu adresa!' }]}>
                  <Input />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={8}>
                <Form.Item label='IBAN' name='bankAccount'>
                  <Input onChange={(e) => handleBankAccountChange(e.target.value)} />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={8}>
                <Form.Item label='Banca' name='bank'>
                  <Input />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={8}>
                <Form.Item label='Telefon' name='phone' rules={[{ required: true, message: 'Introdu telefon!' }]}>
                  <Input />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={8}>
                <Form.Item label='Email' name='email' rules={[{ required: true, message: 'Introdu email!' }]}>
                  <Input />
                </Form.Item>
              </Col>
            </>
          )}
        </Row>
        <Form.Item>
          <Button type='primary' htmlType='submit'>
            Finalizare
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};

export default ProviderForm;
